import { PostHogProvider } from "posthog-js/react";
import { posthog, PostHogConfig } from "posthog-js";
import { useEffect, type ReactNode } from "react";
import { TrelloIframeClientManager } from "../../api/trello";
import { ENVS } from "@v2/shared/environment.config";
import { isRecordingSessionsToPosthogEnabled } from "@v2/shared/config/features.config";

const options: Partial<PostHogConfig> = {
  api_host: process.env.PUBLIC_POSTHOG_HOST,
  autocapture: false,
  capture_pageview: "history_change",
  loaded: function (ph) {
    ph.set_config({ disable_session_recording: true });
    // if (process.env.ENVIRONMENT !== "production") {
    // }
    // AIDEV-NOTE: Defaults-only init. Do not flip opt-in/out at runtime.
    // ph.opt_out_capturing() and ph.opt_in_capturing() seem to be meant to toggle statically at startup
    // and will not work when re-set later (for example will not work inside of ph.onFeatureFlags(() => ...))
  },
};

type PosthogCustomProviderProps = {
  children: ReactNode;
};

export class PosthogManager {
  private static initialized = false;
  private static visibilityListenerAttached = false;
  private static lastIdentifiedMemberId: string | null = null;

  static getClient() {
    return this.ensureInitialized();
  }

  static identifyCurrentMember() {
    const client = this.getClient();

    try {
      if (client.has_opted_out_capturing?.()) return;
      const iframeClient = TrelloIframeClientManager.getClient();
      const context = iframeClient?.getContext?.();
      const memberId = context?.member;

      if (!memberId) return;

      const currentDistinctId = client.get_distinct_id?.();
      if (
        this.lastIdentifiedMemberId === memberId &&
        (!currentDistinctId || currentDistinctId === memberId)
      ) {
        return;
      }

      client.identify(memberId);
      client.reloadFeatureFlags?.();
      this.lastIdentifiedMemberId = memberId;
    } catch {
      // noop
    }
  }

  static startSessionRecording() {
    if (!isRecordingSessionsToPosthogEnabled()) return;
    this.ensureInitialized();
    posthog.startSessionRecording();
  }

  static stopSessionRecording() {
    posthog.stopSessionRecording();
  }

  private static ensureInitialized() {
    if (this.initialized) {
      return posthog;
    }

    posthog.init(process.env.PUBLIC_POSTHOG_KEY!, options);
    if (ENVS.gitCommit) {
      posthog.register({ git_commit: ENVS.gitCommit, app: "bulk-actions" });
    }
    this.initialized = true;

    this.attachVisibilityListener();
    return posthog;
  }

  private static attachVisibilityListener() {
    if (this.visibilityListenerAttached || typeof document === "undefined") {
      return;
    }

    const handler = () => {
      if (document.visibilityState === "hidden") {
        posthog.reset();
        this.lastIdentifiedMemberId = null;
        return;
      }

      if (document.visibilityState === "visible") {
        // AIDEV-NOTE: Trello may restore iframe with new member context; re-identify after reset.
        setTimeout(() => this.identifyCurrentMember(), 0);
      }
    };

    document.addEventListener("visibilitychange", handler);
    this.visibilityListenerAttached = true;
  }
}

export function getPosthogClient() {
  return PosthogManager.getClient();
}

export const PosthogCustomProvider = ({
  children,
}: PosthogCustomProviderProps) => {
  const client = PosthogManager.getClient();

  return (
    <PostHogProvider
      client={client}
      // apiKey={process.env.PUBLIC_POSTHOG_KEY!}
      // options={{ ...options }}
    >
      {children}
    </PostHogProvider>
  );
};

// AIDEV-NOTE: posthog.identify is called only after trello-iframe is ready
export function WithPosthogIdentify({ children }: { children: ReactNode }) {
  useEffect(() => {
    PosthogManager.identifyCurrentMember();
  }, []);

  return children;
}
